import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/nextjs';
import { useSetAtom } from 'jotai';
import useIsOpenModal from 'src/hooks/useIsOpenModal';
import useLogin from 'src/hooks/useLogin';
import { closeModalAtom, showModalAtom } from 'src/stores/modal/atoms';
import { ModalType } from 'src/types/Modal';
import { LoginUserModalInitPromise, ModalInitResult } from './types';
import useAssistantConsentModal from './useAssistantConsentModal';
import useCmpConsentsModal from './useCmpConsentsModal';
import useCommunityGuidelineModal from './useCommunityGuidelineModal';
import useCreatorOnboardingModal from './useCreatorOnboardingModal';
import useFaceAgeVerificationModal from './useFaceAgeVerificationModal';
import useFeatureSettingsPopupModal from './useFeatureSettingsPopupModal';
import useNoticePopupModal from './useNoticePopupModal';
import useProfileInputInterestsModal from './useProfileInputInterestsModal';
import usePushNotificationPermissionModal from './usePushNotificationPermissionModal';
import useSignupInterestsModal from './useSignupInterestsModal';
import useUpdatedConsentModal from './useUpdatedConsentModal';
import useUpdatedLegalNoticeModal from './useUpdatedLegalNoticeModal';
import useWebToAppModal from './useWebToAppModal';
const useLoginUserOrderedModal = () => {
  const [promiseQueue, setPromiseQueue] = useState<LoginUserModalInitPromise[]>([]);
  const [modalQueue, setModalQueue] = useState<ModalInitResult[]>([]);
  const [currentModalKey, setCurrentModalKey] = useState<ModalType | null>(null);
  const isCurrentModalOpened = useIsOpenModal(currentModalKey as ModalType);
  const showModal = useSetAtom(showModalAtom);
  const closeModal = useSetAtom(closeModalAtom);
  const {
    isLogin,
    isUserJustSignedUp
  } = useLogin();
  const {
    initializeModalPromise: initSignupInterestsModal
  } = useSignupInterestsModal();
  const {
    initializeModalPromise: initProfileInputInterestsModal
  } = useProfileInputInterestsModal();
  const {
    initializeModalPromise: initCommunityGuidelineModal
  } = useCommunityGuidelineModal();
  const {
    initializeModalPromise: initFaceAgeVerificationModal
  } = useFaceAgeVerificationModal();
  const {
    initializeModalPromise: initCmpConsentsModal
  } = useCmpConsentsModal();
  const {
    initializeModalPromise: initUpdatedConsentModal
  } = useUpdatedConsentModal();
  const {
    initializeModalPromise: initUpdatedLegalNoticeModal
  } = useUpdatedLegalNoticeModal();
  const {
    initializeModalPromise: initFeatureSettingsModal
  } = useFeatureSettingsPopupModal();
  const {
    initializeModalPromise: initNoticePopupModal
  } = useNoticePopupModal();
  const {
    initializeModalPromise: initCreatorOnboardingModal
  } = useCreatorOnboardingModal();
  const {
    initializeModalPromise: initWebToAppModal
  } = useWebToAppModal();
  const {
    initializeModalPromise: initPushNotificationPermissionModal
  } = usePushNotificationPermissionModal();
  const {
    initializeModalPromise: initAssistantConsentModal
  } = useAssistantConsentModal();
  useEffect(() => {
    if (window.AzarJs) return;
    setModalQueue([]);
    if (isLogin) {
      setPromiseQueue([
      // 가입 완료 시 관심사 선택 모달 노출, 가이드 모달 노출
      ...(isUserJustSignedUp ? [initSignupInterestsModal(), initCommunityGuidelineModal()] : []), initAssistantConsentModal(), initFaceAgeVerificationModal(), initCmpConsentsModal(),
      // 가입 완료 시점에 노출되지만 응답 안 하고 이탈한 유저 고려
      initUpdatedConsentModal(),
      // 로그인 유저 관심사 등록 하지 않았을 경우 관심사 선택 모달 노출
      ...(!isUserJustSignedUp ? [initProfileInputInterestsModal()] : []), initUpdatedLegalNoticeModal(), initNoticePopupModal(), initFeatureSettingsModal(), initCreatorOnboardingModal(), initWebToAppModal(), initPushNotificationPermissionModal()]);
    } else {
      setPromiseQueue([]);
    }
    setCurrentModalKey(prev => {
      if (prev) {
        closeModal(prev);
      }
      return null;
    });
  }, [isLogin, initSignupInterestsModal, initProfileInputInterestsModal, initCmpConsentsModal, initFaceAgeVerificationModal, initUpdatedConsentModal, initUpdatedLegalNoticeModal, initFeatureSettingsModal, initNoticePopupModal, initCreatorOnboardingModal, initWebToAppModal, initPushNotificationPermissionModal, closeModal, isUserJustSignedUp, initCommunityGuidelineModal, initAssistantConsentModal]);
  useEffect(() => {
    // 방금 가입한 유저의 경우 가입 플로우가 온전히 마무리되고 나서 모달들 노출 시작
    if (isCurrentModalOpened) return;
    if (!modalQueue.length) return;
    const [nextModal, ...rest] = modalQueue;
    showModal(nextModal.modalProps, nextModal.eventParams);
    setCurrentModalKey(nextModal.modalProps.key);
    setModalQueue(rest);
  }, [isCurrentModalOpened, modalQueue, showModal]);
  useEffect(() => {
    if (!promiseQueue.length) return;
    let isCanceled = false;
    const [first, ...rest] = promiseQueue;
    first.promise.then(modals => {
      if (isCanceled) return;
      if (!modals) return;
      setModalQueue(prev => [...prev, ...modals]);
    }).catch(err => {
      if (isCanceled) return;
      Sentry.captureException(err);
    }).finally(() => {
      if (!isCanceled) {
        setPromiseQueue(rest);
      }
    });
    return () => {
      isCanceled = true;
    };
  }, [promiseQueue]);
};
export default useLoginUserOrderedModal;